import React, { createContext, useContext, useMemo } from 'react'
import { ColorFormat } from '@sketch/scene-model'
import useLocalStorage from 'react-use/lib/useLocalStorage'

/**
 * The user's preferred colour notation, persisted across sessions.
 *
 * Deliberately not exported: this is the backing store, and having two
 * components call it independently gives you two pieces of state that only
 * agree because they happen to read the same key — the bug
 * `ColorFormatProvider` exists to prevent. Components use `useColorFormat`,
 * which reads the one instance held by the provider.
 */
function useUserColorFormat() {
  const [userColor, setUserColor] = useLocalStorage(
    'inspector_color_format',
    ColorFormat.HEX
  )

  return [userColor ?? ColorFormat.HEX, setUserColor] as const
}

type ColorFormatContextValue = {
  colorFormat: ColorFormat
  onColorFormatChange: (format: ColorFormat) => void
}

const colorFormatContext = createContext<ColorFormatContextValue | undefined>(
  undefined
)

/**
 * Holds the colour format for the inspector.
 *
 * The format used to be read at the top of the sidebar and threaded down as a
 * `colorFormat` / `onColorFormatChange` prop pair through ten components — the
 * style sections, every colour attribute, and the gradient popover — purely so
 * the leaves could render and change it. Worse, the sidebar read it twice, so
 * the two halves of the tree held separate state.
 */
export function ColorFormatProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [colorFormat, setColorFormat] = useUserColorFormat()

  const value = useMemo(
    () => ({ colorFormat, onColorFormatChange: setColorFormat }),
    [colorFormat, setColorFormat]
  )

  return (
    <colorFormatContext.Provider value={value}>
      {children}
    </colorFormatContext.Provider>
  )
}

export function useColorFormat() {
  const value = useContext(colorFormatContext)

  if (!value) {
    throw new Error('useColorFormat must be used within a ColorFormatProvider')
  }

  return value
}
